import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { notificationActions } from '../store/notification-slice';
import '../../dist/assets/stylesheets/css/notification.css';

function NotificationPanel() {
    const dispatch = useDispatch();
    const notification = useSelector((state) => state.notification);
    const [mounted, setMounted] = useState(false);
    const [phase, setPhase] = useState('hidden');
    const dismissTimerRef = useRef(null);
    const enterFrameRef = useRef(null);
    const exitFallbackTimerRef = useRef(null);
    const exitCompleteRef = useRef(false);
    const enterGenerationRef = useRef(0);

    useEffect(() => setMounted(true), []);

    const clearDismissTimer = useCallback(() => {
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
    }, []);

    const requestDismiss = useCallback(() => {
        clearDismissTimer();
        enterGenerationRef.current += 1;
        if (enterFrameRef.current) {
            cancelAnimationFrame(enterFrameRef.current);
            enterFrameRef.current = null;
        }
        setPhase('exiting');
    }, [clearDismissTimer]);

    useEffect(() => {
        if (!notification.show) {
            clearDismissTimer();
            setPhase('hidden');
            return undefined;
        }
        exitCompleteRef.current = false;
        const gen = (enterGenerationRef.current += 1);
        setPhase('entering');
        enterFrameRef.current = requestAnimationFrame(() => {
            enterFrameRef.current = requestAnimationFrame(() => {
                if (enterGenerationRef.current !== gen) return;
                setPhase('visible');
            });
        });
        return () => {
            if (enterFrameRef.current) {
                cancelAnimationFrame(enterFrameRef.current);
            }
        };
    }, [notification.show, notification.message, notification.status, clearDismissTimer]);

    const finishExit = useCallback(() => {
        if (exitCompleteRef.current) return;
        exitCompleteRef.current = true;
        if (exitFallbackTimerRef.current) {
            clearTimeout(exitFallbackTimerRef.current);
            exitFallbackTimerRef.current = null;
        }
        dispatch(notificationActions.closeNotification());
        setPhase('hidden');
    }, [dispatch]);

    useEffect(() => {
        if (phase !== 'exiting') {
            return undefined;
        }
        const ms = 480;
        exitFallbackTimerRef.current = setTimeout(() => finishExit(), ms);
        return () => {
            if (exitFallbackTimerRef.current) {
                clearTimeout(exitFallbackTimerRef.current);
                exitFallbackTimerRef.current = null;
            }
        };
    }, [phase, finishExit]);

    useEffect(() => {
        if (phase !== 'visible' || !notification.show) {
            return undefined;
        }
        clearDismissTimer();
        dismissTimerRef.current = setTimeout(() => {
            setPhase('exiting');
        }, 4000);
        return clearDismissTimer;
    }, [phase, notification.show, clearDismissTimer]);

    const onTransitionEnd = (e) => {
        if (phase !== 'exiting') return;
        if (e.target !== e.currentTarget) return;
        finishExit();
    };

    if (!mounted) {
        return null;
    }
    if (!notification.show && phase === 'hidden') {
        return null;
    }

    const notificationType = {
        error: 'Error!',
        success: 'Success!',
    };
    const typeLabel = notification.status ? notificationType[notification.status] || 'Notice' : 'Notice';

    const phaseClass =
        phase === 'entering' ? 'phase-entering' : phase === 'exiting' ? 'phase-exiting' : 'phase-visible';

    const panel = (
        <div className="notification-panel-container">
            <div
                className={`notification-panel ${notification.status} ${phaseClass}`}
                onTransitionEnd={onTransitionEnd}
            >
                <Icon icon="mingcute:warning-line" />
                <span className="type">{typeLabel}</span>
                <span className="info">{notification.message}</span>
                <button
                    type="button"
                    className="notification-close-btn"
                    data-testid="notification-close"
                    onClick={requestDismiss}
                    aria-label="Dismiss notification"
                >
                    <Icon className="close" icon="iconamoon:close-duotone" />
                </button>
            </div>
        </div>
    );

    return createPortal(panel, document.body);
}

export default NotificationPanel;
